import { useLocation } from "react-router-dom";
import type { AppLocationState } from "~/types/router";

/**
 * A typed wrapper around React Router's useLocation hook that provides type safety
 * for location state. This hook ensures that the location state is properly typed
 * according to the specified state interface.
 *
 * @template T - The type of the location state, must extend AppLocationState
 * @returns An object containing all location properties with properly typed state
 *
 * @example
 * // Basic usage
 * const location = useTypedLocation();
 * const from = location.state?.from;
 *
 * // With specific state type
 * const location = useTypedLocation<AppointmentLocationState>();
 * const filters = location.state?.filters;
 *
 * @see AppLocationState - Base interface for all location states
 * @see AppointmentLocationState - State type for appointment-related pages
 * @see LocationLocationState - State type for location-related pages
 * @see PhysicianLocationState - State type for physician-related pages
 */
export const useTypedLocation = <T extends AppLocationState>() => {
  const location = useLocation();
  const state = location.state as T | null;

  return { ...location, state };
};
